// User roles
export type Role = 'Admin' | 'SecurityOfficer' | 'Analyst' | 'AgentOperator' | 'Viewer';

// Permission strings for feature/action level access control
export type Permission =
  | 'view:dashboard'
  | 'view:policies'
  | 'create:policy'
  | 'update:policy'
  | 'delete:policy'
  | 'view:agents'
  | 'deploy:agent'
  | 'approve:agent-deploy'
  | 'view:audit-logs'
  | 'view:violations'
  | 'view:dlp-data'
  | 'export:dlp-data'
  | 'manage:users'
  | 'manage:settings'
  | 'view:mcp-servers'
  | 'configure:mcp-servers';

// User profile (contact details + avatar)
export interface UserProfile {
  email?: string;                    // Optional contact email
  phone?: string;                    // Optional contact phone
  pictureUrl?: string;               // Avatar URL (served from /api/admin/user/picture/:userId)
  displayName?: string;              // User's display name (from SAML or manual)
}

// User attributes (role + permissions + resource scoping)
export interface UserAttributes {
  userId: string;                    // from JWT agentId claim
  role: Role;                        // User's role
  team?: string;                     // team-a, team-b, etc. (optional)
  dataClassification?: string[];     // ['public', 'confidential', 'sensitive'] (what user can view)
  canApproveActions?: boolean;       // For Security Officer / Admin
  resourceScope?: string[];          // Which agent IDs / policies / teams visible
  profile?: UserProfile;             // Contact details + avatar
  permissions?: Permission[];        // Calculated permissions for this user
}

// Token payload (from backend)
export interface TokenPayload {
  agentId: string;
  orgId: string;
  iat?: number;
  exp?: number;
}

// API response from GET /api/admin/user/context
export interface UserContextResponse {
  userId: string;
  role: Role;
  team?: string;
  dataClassification?: string[];
  canApproveActions?: boolean;
  resourceScope?: string[];
  profile?: UserProfile;
  permissions?: Permission[];
}

// Permission mappings: role -> allowed permissions
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  'Admin': [
    'view:dashboard',
    'view:policies',
    'create:policy',
    'update:policy',
    'delete:policy',
    'view:agents',
    'deploy:agent',
    'approve:agent-deploy',
    'view:audit-logs',
    'view:violations',
    'view:dlp-data',
    'export:dlp-data',
    'manage:users',
    'manage:settings',
    'view:mcp-servers',
    'configure:mcp-servers',
  ],
  'SecurityOfficer': [
    'view:dashboard',
    'view:policies',
    'create:policy',
    'update:policy',
    'view:agents',
    'approve:agent-deploy',
    'view:audit-logs',
    'view:violations',
    'view:dlp-data',
    'view:mcp-servers',
    'configure:mcp-servers',
  ],
  'Analyst': [
    'view:dashboard',
    'view:policies',
    'view:agents',
    'view:audit-logs',
    'view:violations',
    'view:mcp-servers',
  ],
  'AgentOperator': [
    'view:dashboard',
    'view:agents',
    'deploy:agent',
    'view:audit-logs',
  ],
  'Viewer': [
    'view:dashboard',
  ],
};

// Role metadata for user-management UIs (label + short description, ordered by privilege)
export const ROLE_METADATA: { role: Role; label: string; description: string }[] = [
  { role: 'Admin', label: 'Admin', description: 'Full control, incl. user & system management' },
  { role: 'SecurityOfficer', label: 'Security Officer', description: 'Define policies, approve sensitive actions' },
  { role: 'Analyst', label: 'Analyst', description: 'View dashboards, investigate, read-only actions' },
  { role: 'AgentOperator', label: 'Agent Operator', description: 'Deploy & monitor agents; no policy access' },
  { role: 'Viewer', label: 'Viewer', description: 'Read-only dashboard access' },
];

export const ALL_ROLES: Role[] = ROLE_METADATA.map((r) => r.role);

export const roleLabel = (role: Role): string =>
  ROLE_METADATA.find((r) => r.role === role)?.label || role;

// Helper function to check if user has permission
export const hasPermission = (
  userAttributes: UserAttributes,
  permission: Permission
): boolean => {
  if (!userAttributes) {
    return false;
  }

  // Admin always has all permissions
  if (userAttributes.role === 'Admin') {
    return true;
  }

  // Check base permissions from role
  const rolePerms = ROLE_PERMISSIONS[userAttributes.role] || [];
  return rolePerms.includes(permission);
};

// Helper function to check sensitive data access with ABAC
export const canViewDLP = (userAttributes: UserAttributes): boolean => {
  if (!userAttributes) {
    return false;
  }

  // Admin and SecurityOfficer can always view DLP
  if (userAttributes.role === 'Admin' || userAttributes.role === 'SecurityOfficer') {
    return true;
  }

  // Analyst with 'sensitive' classification can view DLP
  if (userAttributes.role === 'Analyst' && userAttributes.dataClassification?.includes('sensitive')) {
    return true;
  }

  return false;
};
